import React, {
  useEffect,
  useRef,
  useState,
  useLayoutEffect,
  useCallback,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import IcomoonReact from 'icomoon-react';
import iconSet from '../../../shared/images/teambuildr-selection.json';
import {
  initializeWeekRange,
  setPlannerMode,
  setPlannerExamplesModalOpen,
  setSelectedCalendarName,
} from '../ducks/plannerActions';
import { getWeeksInRange } from '../utils/weekUtils';
import CalendarChooser from './presentational/CalendarChooser';
import ModeToggle from './presentational/ModeToggle';
import PlanView from './PlanView';
import ReviewView from './ReviewView';
import PlanViewSkeleton from './presentational/PlanViewSkeleton';
import ReviewViewSkeleton from './presentational/ReviewViewSkeleton';
import PreviewTable from './PreviewTable';
import ExportModal from '../modals/ExportModal';
import BulkDeleteModal from '../modals/BulkDeleteModal';
import BulkCopyModal from '../modals/BulkCopyModal';
import OnboardingModal from '../../../shared/_TBComponents/OnboardingModal';
import HeaderText from '../../../shared/_TBComponents/HeaderText';
import plannerExamplesContent from './examplesContent';
import usePlannerCalendarPersistence from '../hooks/usePlannerCalendarPersistence';
import usePlannerExport from '../hooks/usePlannerExport';
import useNetworkStatus from '../../../shared/utils/networkStatus';

// Height of the global top nav bar
// Matches padding-top on PlannerContainer and top on PlannerHeader
const NAV_BAR_HEIGHT = 49;

// Height of the action button strip below the main header
// Breakdown: 8px padding top + 32px buttons + 8px padding bottom
const BUTTON_STRIP_HEIGHT = 48;

const PlannerContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  padding-top: 49px;
  background: #f6f8fb;
  font-family: 'Nunito Sans', sans-serif;
  color: #444444;
`;

const PlannerHeader = styled.header`
  position: fixed;
  top: 49px;
  left: 0;
  right: 0;
  z-index: 100;
  background: #ffffff;
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
  padding: 14px 24px;
`;

const HeaderInner = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 8px;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const ViewingLabel = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 150%;
  display: flex;
  align-items: center;
  color: #9e9e9e;
  white-space: nowrap;
`;

const TodayButton = styled.button`
  background: white;
  color: #444444;
  border: 1px solid #eeeeee;
  padding: 6px 16px;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.48px;
  cursor: pointer;
  font-family: 'Nunito Sans', sans-serif;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  line-height: 1.5;
  height: 40px;

  &:hover {
    background: #f5f5f5;
    border-color: #e0e0e0;
  }

  &:active {
    background: #eeeeee;
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px rgba(93, 69, 249, 0.3);
  }
`;

const ExportButton = styled.button`
  background: #444444;
  color: #ffffff;
  border: none;
  padding: 6px 16px;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.48px;
  cursor: pointer;
  font-family: 'Nunito Sans', sans-serif;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  white-space: nowrap;
  line-height: 1.5;
  height: 40px;

  &:hover {
    background: #333333;
  }

  &:active {
    background: #222222;
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px rgba(93, 69, 249, 0.3);
  }
`;

const ExamplesButton = styled.button`
  background: white;
  color: #444444;
  border: 1px solid #eeeeee;
  padding: 6px 16px;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.48px;
  cursor: pointer;
  font-family: 'Nunito Sans', sans-serif;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  white-space: nowrap;
  line-height: 1.5;
  height: 40px;
  box-shadow: 0 0 1px rgba(48, 49, 51, 0.05), 0 1px 3px rgba(48, 49, 51, 0.1);

  &:hover {
    background: #f5f5f5;
    border-color: #e0e0e0;
  }

  &:active {
    background: #eeeeee;
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px rgba(93, 69, 249, 0.3);
  }
`;

const ButtonStrip = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  z-index: 99;
  padding: 8px 16px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ActionButtonGroup = styled.div`
  background: #ffffff;
  display: flex;
  align-items: center;
  gap: 4px;
  border-radius: 6px;
  padding: 4px;
`;

const ActionButton = styled.button`
  height: 32px;
  width: 32px;
  border-radius: 4px;
  border: none;
  background: none;
  padding: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  color: #BDBDBD;

  &:hover {
    background: ${(props) => props.hoverColor || 'rgba(119, 167, 255, 0.2)'};
    color: ${(props) => props.iconHoverColor || '#77A7FF'};
  }
`;

const PlannerContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: auto;
`;

const ExportLoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${(props) => (props.show ? '1' : '0')};
  visibility: ${(props) => (props.show ? 'visible' : 'hidden')};
  transition: opacity 0.2s ease, visibility 0.2s ease;
`;

const ExportLoadingIndicator = styled.div`
  background: #444444;
  color: white;
  padding: 20px 32px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: 'Nunito Sans', sans-serif;
`;

/**
 * PlannerPage Component
 *
 * Main container component for the planner feature.
 * Handles calendar selection, mode switching, and window selection.
 */
const PlannerPage = ({ currentUser }) => {
  const dispatch = useDispatch();
  const { isOnline } = useNetworkStatus();

  const selectedCalendarId = useSelector(
    (state) => state.planner.ui.selectedCalendarId,
  );
  const selectedCalendarName = useSelector(
    (state) => state.planner.ui.selectedCalendarName,
  );
  const mode = useSelector((state) => state.planner.ui.mode);
  const loadedWeekRange = useSelector(
    (state) => state.planner.ui.loadedWeekRange,
  );
  const isLoading = useSelector((state) => state.planner.ui.isPlannerLoading);
  const planId = useSelector((state) => state.planner.data.planId);
  const isPlannerExamplesModalOpen = useSelector(
    (state) => state.planner.ui.isPlannerExamplesModalOpen,
  );
  const orgLogo = currentUser?.userSkin?.logo || null;

  // Calculate weeks array from loaded range
  const weeks = loadedWeekRange.fromWeek && loadedWeekRange.toWeek
    ? getWeeksInRange(loadedWeekRange.fromWeek, loadedWeekRange.toWeek)
    : [];

  const [headerHeight, setHeaderHeight] = useState(136);
  const headerRef = useRef(null);
  const [isCurrentWeekVisible, setIsCurrentWeekVisible] = useState(true);
  const [isBulkCopyModalOpen, setIsBulkCopyModalOpen] = useState(false);
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);

  // Calendar persistence hook
  const onInitializeWeekRange = useCallback(() => {
    dispatch(initializeWeekRange());
  }, [dispatch]);

  const {
    isRestoringCalendar,
    hasInitialized,
    handleCalendarSelect,
    getStorageKeyForAccount,
  } = usePlannerCalendarPersistence({
    currentUser,
    loadedWeekRange,
    onInitializeWeekRange,
  });

  // Export hook
  const {
    isExportModalOpen,
    exportLoadingMessage,
    exportDefaultFromWeek,
    exportDefaultToWeek,
    isFetchingBlock,
    handleExport,
    handleCloseExportModal,
    handleExportToPDF,
  } = usePlannerExport({
    planId,
    selectedCalendarName,
    accountCode: currentUser?.accountCode,
    orgLogo,
    calendarNameStorageKey: getStorageKeyForAccount('last_calendar_name'),
  });

  const areControlsDisabled = isRestoringCalendar || isLoading;
  const isExportDisabled = isFetchingBlock
    || !selectedCalendarId
    || isRestoringCalendar
    || !isOnline;

  useLayoutEffect(() => {
    if (!headerRef.current) return undefined;

    const measureHeader = () => {
      const { height } = headerRef.current.getBoundingClientRect();
      setHeaderHeight(height + 16);
    };

    measureHeader();

    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(measureHeader)
      : null;
    if (resizeObserver) {
      resizeObserver.observe(headerRef.current);
    }
    window.addEventListener('resize', measureHeader);

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener('resize', measureHeader);
    };
  }, []);

  const handleModeToggle = (nextMode) => {
    if (nextMode !== mode) {
      dispatch(setPlannerMode(nextMode));
    }
  };

  // Listen for current week visibility updates from child views
  useEffect(() => {
    const handleVisibilityChange = (event) => {
      setIsCurrentWeekVisible(event.detail.isVisible);
    };

    window.addEventListener(
      'planner:weekVisibilityChange',
      handleVisibilityChange,
    );
    return () => {
      window.removeEventListener(
        'planner:weekVisibilityChange',
        handleVisibilityChange,
      );
    };
  }, []);

  const handleOpenExamples = () => {
    dispatch(setPlannerExamplesModalOpen(true));
  };

  const handleCloseExamples = () => {
    dispatch(setPlannerExamplesModalOpen(false));
  };

  const hasAnnualPlannerAccess = currentUser?.organizationHasAccessToAnnualPlanner === true;

  const examplesModalContent = plannerExamplesContent;

  // Auto-open examples/upgrade modal for non-platinum users
  useEffect(() => {
    // Show upgrade modal for non-platinum users once we know their access level.
    const shouldAutoOpen = currentUser
      && !hasAnnualPlannerAccess
      && hasInitialized;

    if (shouldAutoOpen) {
      dispatch(setPlannerExamplesModalOpen(true));
    }
  }, [
    currentUser,
    hasAnnualPlannerAccess,
    hasInitialized,
    dispatch,
  ]);

  // headerHeight = actualHeaderHeight + 16 (gap). Strip sits immediately below the actual header.
  const buttonStripTop = NAV_BAR_HEIGHT + headerHeight - 16;

  return (
    <PlannerContainer>
      <PlannerHeader ref={headerRef}>
        <HeaderInner>
          <HeaderText fontSize='19px' fontWeight='700' lineHeight='150%' margin='0' padding='0'>The Planner</HeaderText>
          <HeaderRow>
            <HeaderLeft>
              <ViewingLabel>Viewing</ViewingLabel>
              <CalendarChooser
                selectedCalendarId={selectedCalendarId}
                onSelectCalendar={handleCalendarSelect}
                onCalendarNameFound={(name) => {
                  // Update calendar name in Redux and localStorage when found
                  const storageKey = getStorageKeyForAccount(
                    'last_calendar_name',
                  );
                  if (name && !selectedCalendarName && storageKey) {
                    dispatch(setSelectedCalendarName(name));
                    localStorage.setItem(storageKey, name);
                  }
                }}
                accountCode={currentUser?.accountCode}
                isDisabled={!hasAnnualPlannerAccess || areControlsDisabled || !isOnline}
              />
              <ModeToggle
                mode={mode}
                onModeChange={areControlsDisabled ? () => {} : handleModeToggle}
              />
              {!isCurrentWeekVisible && !areControlsDisabled && (
                <TodayButton
                  onClick={() => {
                    // Trigger today scroll in child view
                    window.dispatchEvent(
                      new CustomEvent('planner:jumpToToday'),
                    );
                  }}
                >
                  Today
                </TodayButton>
              )}
            </HeaderLeft>
            <HeaderRight>
              <ExamplesButton
                onClick={handleOpenExamples}
                disabled={areControlsDisabled}
              >
                <IcomoonReact
                  iconSet={iconSet}
                  icon='Lightbulb'
                  size={16}
                  color='#444444'
                />
                Examples
              </ExamplesButton>
              <ExportButton onClick={handleExport} disabled={isExportDisabled}>
                <IcomoonReact
                  iconSet={iconSet}
                  icon='download'
                  size={14}
                  color='#FFFFFF'
                />
                Export PDF
              </ExportButton>
            </HeaderRight>
          </HeaderRow>
        </HeaderInner>
      </PlannerHeader>

      <ButtonStrip style={{ top: buttonStripTop }}>
        <ActionButtonGroup>
          <ActionButton
            type='button'
            data-tooltip-id='planner-copy-btn'
            onClick={() => setIsBulkCopyModalOpen(true)}
          >
            <IcomoonReact
              iconSet={iconSet}
              size={24}
              icon='duplicate'
              color='currentColor'
            />
          </ActionButton>
          <ActionButton
            type='button'
            data-tooltip-id='planner-clear-btn'
            hoverColor='rgba(255, 5, 5, 0.2)'
            iconHoverColor='#FF0505'
            onClick={() => setIsBulkDeleteModalOpen(true)}
          >
            <IcomoonReact
              iconSet={iconSet}
              size={24}
              icon='trashcan'
              color='currentColor'
            />
          </ActionButton>
        </ActionButtonGroup>
        <ReactTooltip
          id='planner-copy-btn'
          place='bottom'
          content='Copy'
          style={{
            backgroundColor: 'rgba(68, 68, 68, 1)',
            color: 'white',
            fontFamily: 'Nunito Sans',
            fontSize: '13px',
            zIndex: 1000,
          }}
        />
        <ReactTooltip
          id='planner-clear-btn'
          place='bottom'
          content='Clear'
          style={{
            backgroundColor: 'rgba(68, 68, 68, 1)',
            color: 'white',
            fontFamily: 'Nunito Sans',
            fontSize: '13px',
            zIndex: 1000,
          }}
        />
      </ButtonStrip>

      <PlannerContent
        style={{
          marginTop: headerHeight + BUTTON_STRIP_HEIGHT - 16,
        }}
      >
        {(() => {
          // Show preview when no calendar is selected
          if (!selectedCalendarId && !isRestoringCalendar) {
            return <PreviewTable mode={mode} />;
          }
          if (isLoading || isRestoringCalendar) {
            return mode === 'plan' ? (
              <PlanViewSkeleton />
            ) : (
              <ReviewViewSkeleton />
            );
          }
          // Show actual view
          if (mode === 'plan') {
            return <PlanView />;
          }
          return (
            <ReviewView selectedCalendarId={selectedCalendarId} weeks={weeks} />
          );
        })()}
      </PlannerContent>

      {isBulkDeleteModalOpen && (
        <BulkDeleteModal
          onClose={() => setIsBulkDeleteModalOpen(false)}
        />
      )}

      {isBulkCopyModalOpen && (
        <BulkCopyModal
          onClose={() => setIsBulkCopyModalOpen(false)}
          accountCode={currentUser?.accountCode}
          sourceCalendarId={selectedCalendarId}
          sourceCalendarName={selectedCalendarName}
        />
      )}

      {isExportModalOpen && (
        <ExportModal
          defaultFromWeek={exportDefaultFromWeek}
          defaultToWeek={exportDefaultToWeek}
          onClose={handleCloseExportModal}
          onExport={handleExportToPDF}
        />
      )}

      {isPlannerExamplesModalOpen && (
        <>
          <OnboardingModal
            isActive={isPlannerExamplesModalOpen}
            isOpen={isPlannerExamplesModalOpen}
            content={examplesModalContent}
            onClose={hasAnnualPlannerAccess ? handleCloseExamples : undefined}
            onPrimaryAction={
              hasAnnualPlannerAccess
                ? handleCloseExamples
                : () => {
                  window.open(
                    `${process.env.PHP_APP_URL
                        || 'https://app.teambuildr.com'}/billing`,
                    '_blank',
                  );
                }
            }
            onSecondaryAction={() => {}}
            primaryButtonText={
              hasAnnualPlannerAccess ? 'CLOSE' : 'UPGRADE TO PLATINUM PRO'
            }
            showSingleButton
            showBadge={!hasAnnualPlannerAccess}
            badgeText='AVAILABLE ON PLATINUM PRO'
          />
          <style>
            {`
              .ReactModalPortal {
                z-index: 999 !important;
              }
              .ReactModal__Overlay {
                z-index: 999 !important;
                top: 49px !important;
                height: calc(100vh - 49px) !important;
              }
            `}
          </style>
        </>
      )}

      <ExportLoadingOverlay show={!!exportLoadingMessage}>
        <ExportLoadingIndicator>{exportLoadingMessage}</ExportLoadingIndicator>
      </ExportLoadingOverlay>
    </PlannerContainer>
  );
};

PlannerPage.propTypes = {
  currentUser: PropTypes.shape({
    accountCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    organizationHasAccessToAnnualPlanner: PropTypes.bool,
    admin: PropTypes.number,
    userSkin: PropTypes.shape({
      logo: PropTypes.string,
    }),
  }),
};

PlannerPage.defaultProps = {
  currentUser: null,
};

export default PlannerPage;
